import {
  motion,
  useMotionTemplate,
  useSpring,
  useViewportScroll,
  Variants,
} from 'framer-motion';
import { GatsbyImage } from 'gatsby-plugin-image';
import React, {
  FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { isMobile, isMobileOnly } from 'react-device-detect';
import InView from 'react-intersection-observer';
import tw, { styled } from 'twin.macro';
import {
  AudioPlayer,
  LinkPlus,
  mobileAudioPlayerCss,
  ShareIcons,
} from '~/components/common';
import { getPageExitClick } from '~/components/common/transitions/page-exit';
import { LiveTextPoem, poemWidthCss } from '~/components/live-text-poem';
import { Poem as PoemProps } from '~/config';
import ChevronLeftIcon from '~/images/chevron-left.svg';
import ChevronRightIcon from '~/images/chevron-right.svg';
import { poemHeaderHeightCss } from './common/index';
import useResizeObserver, {
  UseResizeObserverCallback,
} from '@react-hook/resize-observer';
import { useRaf } from '~/hooks/useRaf';
import { AudioContext } from '~/context/AudioContext';

// `isMobile/Only`: the rest of this project should use `isMobileOnly`. This component
// uses `isMobile` because it features scrolljacking, which doesn't work well on touch.

// TODO make this shared w PoemInfoPage if it ends up the same
const springConfig = {
  damping: 20,
  stiffness: 80,
};

type PoemPageTopHalfProps = {
  isEntered: boolean;
  pageContext: PoemProps;
  onHeaderInViewChange: (
    isHeaderInViewReady: boolean,
    isHeaderInView: boolean,
  ) => void;
};

export const PoemPageTopHalf: FC<PoemPageTopHalfProps> = ({
  isEntered,
  pageContext,
  onHeaderInViewChange,
}) => {
  const topContentRef = useRef<HTMLDivElement | null>(null);
  const infoHeaderRef = useRef<HTMLDivElement | null>(null);
  const stuckInfoHeaderRef = useRef<HTMLDivElement | null>(null);
  const [topContentHeight, setTopContentHeight] = useState(0);
  const [infoHeaderHeight, setInfoHeaderHeight] = useState(0);
  const [isHeaderInView, setHeaderInView] = useState(true);
  const [isHeaderInViewReady, setHeaderInViewReady] = useState(false);
  const [redactionComplete, setRedactionComplete] = useState(false);
  const { scrollY } = useViewportScroll();
  const offsetY = useSpring(0, springConfig);
  const transform = useMotionTemplate`translateY(${offsetY}px)`;
  const { stop } = useContext(AudioContext);

  // ResizeObserver works correctly during hot reload due to update delays in the poem component
  const handleResize = useCallback<UseResizeObserverCallback>((entry) => {
    setTopContentHeight(entry.contentRect.height);
    if (infoHeaderRef.current) {
      setInfoHeaderHeight(infoHeaderRef.current.clientHeight);
    }
  }, []);
  useResizeObserver(topContentRef, handleResize);

  const handleHeaderInViewChange = useCallback(
    (inView: boolean, entry: IntersectionObserverEntry) => {
      setHeaderInViewReady(!!entry);
      setHeaderInView(inView);
      onHeaderInViewChange(!!entry, inView);
    },
    [onHeaderInViewChange],
  );

  const factsImage = useMemo(
    () =>
      pageContext.context_image
        ? pageContext.context_image?.image?.childImageSharp.gatsbyImageData
        : pageContext.timeline_image?.image?.childImageSharp.gatsbyImageData,
    [pageContext.context_image, pageContext.timeline_image],
  );

  const poemHeaderVariants = useMemo(
    () =>
      ({
        // see also: perspective, tranform-origin below
        out: {
          y: -30, // based on mobile /poem/cumming-v-richmond-county-board-of-education
          scaleY: 2,
          rotateX: 30,
        },
        in: {
          y: 0,
          scaleY: 1,
          rotateX: 0,
          transition: {
            duration: 0.3,
            ease: 'circOut',
          },
        },
      } as Variants),
    [],
  );
  const poemHeaderAudioVariants = useMemo(
    () =>
      ({
        out: {
          y: -20,
          opacity: 0,
        },
        in: {
          y: 0,
          transition: {
            duration: 0.3,
            ease: 'circOut',
          },
        },
      } as Variants),
    [],
  );

  // used to toggle two versions of the info header on and off to simulate sticky,
  // this technique results in the most stable results
  const handleTick = useCallback(() => {
    if (
      topContentRef.current &&
      infoHeaderRef.current &&
      stuckInfoHeaderRef.current
    ) {
      const isStuck =
        topContentRef.current.getBoundingClientRect().bottom <= 166;
      infoHeaderRef.current.style.display = isStuck ? 'none' : 'block';
      stuckInfoHeaderRef.current.style.display = isStuck ? 'block' : 'none';
    }
  }, []);
  useRaf(true, handleTick);

  const handleScroll = useCallback(
    (val: number) => offsetY.set(-val),
    [offsetY],
  );

  useEffect(() => {
    if (!isMobile) {
      const cleanup = scrollY.onChange(handleScroll);
      return cleanup;
    }
  }, [handleScroll, scrollY]);

  // used in prev/next buttons
  const exitOptions = useMemo(
    () => ({
      onClick: () => {
        stop(); // stopping audio immediately allows it to play via the delayed click (don't update session storage here)
        window.scrollTo(0, 0);
      },
    }),
    [stop],
  );

  const topContent = (
    // Fragment keeps sticky elements at top level of scroll view (no Layout wrapper)
    <>
      {/* On-page header block that slides under the real header */}
      <PoemSection isHeader isDone={redactionComplete}>
        <InView
          as="div"
          // @ts-ignore
          css={poemHeaderCss}
          rootMargin="-180px 0px 0px 0px"
          onChange={handleHeaderInViewChange}
        >
          <CaseHeader
            variants={isHeaderInViewReady ? poemHeaderVariants : undefined}
            initial="in"
            animate={isHeaderInView ? 'in' : 'out'}
            css={[
              tw`text-black`,
              redactionComplete
                ? tw`[border:1px solid #A53686]`
                : tw`[border:1px solid #000000]`,
            ]}
          >
            <H3 css={[redactionComplete && tw`text-white`]}>
              {pageContext.case_name}
            </H3>
            <span css={[redactionComplete && tw`text-brand-coral`]}>
              {pageContext.case_subtitle}
            </span>
          </CaseHeader>
          {pageContext.audio_file && pageContext.read_by && isMobileOnly && (
            <motion.div
              variants={poemHeaderAudioVariants}
              initial="in"
              animate={isHeaderInView ? 'in' : 'out'}
              tw="flex justify-center"
            >
              <AudioPlayer
                audio={pageContext.audio_file}
                readBy={pageContext.read_by}
                customCss={mobileAudioPlayerCss}
              />
            </motion.div>
          )}
        </InView>
      </PoemSection>

      {/* Poem */}
      <PoemSection isDone={redactionComplete}>
        <PoemBackground>
          <LiveTextPoem
            playing={isEntered}
            poemMarkdown={pageContext.live_text}
            // Do NOT change width or add padding on desktop! Redaction engine is set up for fixed width on desk.
            customCss={[isMobileOnly && tw`px-8`]}
            onRedactionComplete={() => setRedactionComplete(true)}
          />
        </PoemBackground>
      </PoemSection>
    </>
  );

  // Image bar with Previous & Next arrow buttons
  const infoHeader = (
    <InfoPagesHeader>
      {factsImage && (
        <GatsbyImage image={factsImage} alt="" tw="absolute inset-0" />
      )}
      <PoemNav>
        {pageContext.prev && (
          <LinkPlus
            onClick={getPageExitClick(pageContext.prev, exitOptions)}
            to={pageContext.prev}
            customCss={poemNavLinkStyles}
          >
            <ChevronLeftIcon alt="previous poem" tw="pr-1 w-[20px] xl:w-full" />
          </LinkPlus>
        )}
        {pageContext.next && (
          <LinkPlus
            to={pageContext.next}
            onClick={getPageExitClick(pageContext.next, exitOptions)}
            customCss={poemNavLinkStyles}
          >
            <ChevronRightIcon alt="next poem" tw="pl-1 w-[20px] xl:w-full" />
          </LinkPlus>
        )}
      </PoemNav>
      {isMobileOnly /* excludes iPad */ && (
        <div tw="text-white bg-black ">
          <ShareIcons poem={pageContext} />
        </div>
      )}
    </InfoPagesHeader>
  );

  return isMobile ? (
    <>
      {topContent}
      {infoHeader}
    </>
  ) : (
    <>
      {/* hidden page scroll sizer for virtualization */}
      <div
        style={{
          width: '100%',
          height: topContentHeight + infoHeaderHeight,
          backgroundColor: redactionComplete ? 'black' : 'white',
        }}
      />

      {/* virtualization wrapper for top content */}
      <motion.div
        ref={topContentRef}
        style={{
          zIndex: 10,
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          transform,
        }}
      >
        {topContent}
      </motion.div>

      {/* virtualization wrapper for info header that scrolls with top content */}
      <motion.div
        ref={infoHeaderRef}
        tw="z-10 fixed block left-0 w-full"
        style={{ top: topContentHeight, transform }}
      >
        {infoHeader}
      </motion.div>
      {/* duplicate "sticky" info header (see note in useRaf above) */}
      <div ref={stuckInfoHeaderRef} tw="z-20 fixed left-0 w-full top-[166px]">
        {infoHeader}
      </div>
    </>
  );
};

const PoemSection = styled(motion.div)<{ isDone: boolean; isHeader?: boolean }>(
  ({ isDone, isHeader }) => [
    // this is a difficult layout - header sizing, padding, margins are touchy  here, and size seems overlap with element below.
    // check w/ a large title like /poem/cumming-v-richmond-county-board-of-education
    isHeader && tw`pt-6 mt-[92px] h-48 md:(pt-0 h-56)`,

    tw`z-10 pb-2 relative w-full max-w-[100vw] overflow-hidden flex justify-center items-start /* 'start' leaves a bigger gap below on short titles but is needed for long ones */ 
    [transition: background-color 1s ease-in-out]`,

    isDone ? tw`bg-black` : tw`bg-white`,
  ],
);

const poemHeaderCss = [
  poemWidthCss,
  poemHeaderHeightCss,
  tw`z-10 relative font-secondary [max-width:350px] md:( top-8) w-full h-[86px] flex flex-col [perspective: 1000px]`,
];

/* LOCKUP - poem title, share button, and prev/next arrows with even space on either side of title */
const PoemNav = tw.div`relative flex justify-between items-center h-full px-[8px] mx-auto md:max-w-3xl lg:max-w-[58rem] xl:(max-w-full mx-8)`;

const poemNavLinkStyles = [
  tw`w-[40px] h-[40px] sm:(w-[48px] h-[48px]) xl:(w-[61px] h-[61px]) bg-brand-coral hover:bg-black focus-visible:bg-black [transition:background .25s ease-in-out] rounded-full flex items-center justify-around`,
];
const CaseHeader = styled(motion.div)(() => [
  tw`[max-width:350px] px-4 py-2 origin-bottom
    text-center font-secondary
  `,
]);

const H3 = tw.h3`[transition:all 1s ease-in-out] text-xl leading-tight md:(text-2xl)`;

const PoemBackground = tw.div`mb-12 md:(mb-20 px-12)`;

const InfoPagesHeader = styled(motion.div)(() => [
  tw`z-20 left-0 w-full`,
  // Sticky on mobile portrait and tablet both orientations; relative on phone landscape and desktop (virtualized)
  // (careful, match `top` to poemHeaderHeights)
  isMobileOnly &&
    tw`portrait:(sticky top-[166px]) landscape:(relative) h-[100px]`, // phone
  isMobile && !isMobileOnly && tw`sticky top-[166px] h-[190px]`, // tablet
  !isMobile && tw`relative h-[190px]`, // desk
]);

export const PoemFooter = tw(motion.div)`
  z-10 fixed
  inset-x-0 bottom-4 flex justify-center px-2 xl:pb-0 
  text-white font-normal capitalize text-sm md:(text-base)`;

export const FooterNav = tw.ul`flex flex-wrap gap-2 justify-center  md:flex-nowrap`;
